import type {
  InvoiceLine,
  GroupedInvoiceLine,
  ConsolidatedRow,
} from "@/types/invoice";

/**
 * Builds the rows for the consolidated ("agrupado") line view: `grouped_lines`
 * describes each group, and `lines` carries both the ungrouped lines and the
 * group-related ones (member originals and/or the real consolidated line, all
 * tagged with the group's `group_id`).
 *
 * Output ordering: each consolidated group first (in backend order), then the
 * ungrouped originals (in their original order) so the consolidated total still
 * reconciles against the invoice total.
 *
 * This function is the ONLY place that reads the `grouped_lines` overlay shape;
 * if the contract moves again, rewrite this builder and the template stays
 * untouched.
 */
export function buildConsolidatedRows(
  lines: InvoiceLine[],
  groupedLines: GroupedInvoiceLine[],
): ConsolidatedRow[] {
  const lineById = new Map<number, InvoiceLine>();
  for (const line of lines) {
    if (line.id != null) lineById.set(line.id, line);
  }

  const renderedGroupIds = new Set(groupedLines.map((group) => group.group_id));
  const consumedLineIds = new Set<number>();

  const groupRows: ConsolidatedRow[] = groupedLines.map((group) => {
    const childLines = group.source_line_ids
      .map((id) => lineById.get(id))
      .filter((line): line is InvoiceLine => Boolean(line));

    for (const id of group.source_line_ids) consumedLineIds.add(id);
    if (group.consolidated_line_id != null) consumedLineIds.add(group.consolidated_line_id);

    return {
      rowKey: `g-${group.group_id}`,
      groupId: group.group_id,
      keyword: group.keyword,
      keywordSource: group.keyword_source,
      costCenter: group.cost_center ?? null,
      account: group.account ?? null,
      linesCount: group.lines_count,
      total: group.total,
      expandable: childLines.length > 0,
      childLines,
      isConsolidated: group.consolidated_line_id != null,
    };
  });

  // Lines that no group claimed render as standalone rows (a "group of one"),
  // so the agrupado view still shows every line and totals reconcile. A line
  // tagged with the group_id of a rendered group (member original OR the real
  // consolidated line) is already represented by that group row; lines pointing
  // at a group the payload didn't send stay visible instead of silently vanishing.
  const standaloneRows: ConsolidatedRow[] = lines
    .filter((line) => {
      if (line.group_id != null && renderedGroupIds.has(line.group_id)) return false;
      return line.id == null || !consumedLineIds.has(line.id);
    })
    .map((line) => {
      // CECO/cuenta are only unambiguous when the line has exactly one split.
      const singleSplit = line.splits?.length === 1 ? line.splits[0] : null;
      return {
        rowKey: `l-${line.id ?? line.number}`,
        groupId: null,
        keyword: line.product || "",
        keywordSource: null,
        costCenter: singleSplit?.cost_center ?? null,
        account: singleSplit?.account ?? null,
        linesCount: 1,
        total: line.total ?? 0,
        expandable: false,
        childLines: [line],
        isConsolidated: false,
      };
    });

  return [...groupRows, ...standaloneRows];
}

/**
 * Flattens (lines + grouped_lines) into the invoice's EFFECTIVE lines — each real amount exactly once.
 *
 * Needed because `lines` carries both sides of a consolidation: the materialized consolidated line AND
 * the member originals it replaced, all tagged with the same `group_id`. Mapping that array as-is
 * double-counts every grouped amount.
 *
 * Distinct from `buildConsolidatedRows` above, which builds display rows (a group row owning its
 * `childLines`) for the "agrupado" table. This one returns plain `InvoiceLine`s for callers that need
 * the underlying records — the rendition association maps them into rendition lines. Both live here
 * because both read the `grouped_lines` overlay: if that contract moves, they move together.
 *
 * Ordering mirrors `buildConsolidatedRows`: grouped lines first (in backend group order), then the
 * ungrouped originals.
 */
export function flattenInvoiceLines(
  lines: InvoiceLine[],
  groupedLines: GroupedInvoiceLine[],
): InvoiceLine[] {
  const lineById = new Map<number, InvoiceLine>();
  for (const line of lines) {
    if (line.id != null) lineById.set(line.id, line);
  }

  // Every id a group mentions — so the ungrouped pass below can't emit it a second time.
  const claimed = new Set<number>();
  const emittedIds = new Set<number>();
  const fromGroups: InvoiceLine[] = [];

  const emit = (line: InvoiceLine | undefined): void => {
    if (!line) return;
    // Guards the pathological case of two groups naming the same source line.
    if (line.id != null) {
      if (emittedIds.has(line.id)) return;
      emittedIds.add(line.id);
    }
    fromGroups.push(line);
  };

  for (const group of groupedLines) {
    for (const id of group.source_line_ids) claimed.add(id);
    if (group.consolidated_line_id != null) claimed.add(group.consolidated_line_id);

    // A materialized group: the consolidated line alone carries the group's amounts, so its member
    // originals must NOT also be emitted — that is the double count.
    const consolidated = group.consolidated_line_id != null
      ? lineById.get(group.consolidated_line_id)
      : undefined;
    if (consolidated) {
      emit(consolidated);
      continue;
    }
    // Either a legacy fase-1 group (no consolidated line exists, the members ARE the real lines), or
    // the payload named a consolidated line it didn't include. Both fall back to the members: dropping
    // the group would silently under-report the invoice total.
    for (const id of group.source_line_ids) emit(lineById.get(id));
  }

  // Lines no group claimed. A line tagged with a `group_id` absent from `grouped_lines` stays visible
  // rather than vanishing — same call `buildConsolidatedRows` makes.
  const ungrouped = lines.filter((line) => line.id == null || !claimed.has(line.id));
  return [...fromGroups, ...ungrouped];
}
